import * as Sentry from "@sentry/nextjs";
import { request } from "graphql-request";
import { jsonToGraphQLQuery } from "json-to-graphql-query";
import { useInfiniteQuery } from "react-query";
import { useSession } from "ui/containers/session";

const endpoint = process.env.NEXT_PUBLIC_API_BASE_URL_ROCKY_GRAPHQL;

// PostgreSQL 40001 = read-replica WAL conflict. Transient - succeeds on retry.
function isReplicaConflict(error) {
  const msg = error?.message || error?.response?.errors?.[0]?.message || "";
  return msg.includes("conflict with recovery");
}

export const memberRequestKeys = {
  email: true,
  createdTS: true,
  profileImgImgixURL: true,
  gender: true,
  dob: true,
  roleName: true,
  membershipStatus: true,
  lastActivityTS: true,
  trialExpirationTS: true,
  tags: true,
  note: true,
  isPushEnabled: true,

  // adds 2s to query time
  // coachRoomID: true,
  // teamRoomID: true,
  // coachRoomLastMessageSentTS: true,
  // coachRoomLastCoachMessageRecievedTS: true,
  // coachRoomLastImpersonatedCoachMessageRecievedTS: true,
  // teamRoomLastMessageSentTS: true,

  // adds 15s to query time
  // activeRoomFlagTypes: true,
  // avgWorkoutRating: true,
  // numWorkoutsCompleted: true,
  // numWorkoutsCompleted7Days: true,
  // numWorkoutsCompleted14Days: true,
  // numWorkoutsCompleted30Days: true,
  // numWorkoutsIncomplete: true,
  // lastCompletedWorkoutID: true,
  // lastCompletedWorkoutWorkoutID: true,
  // lastCompletedWorkoutCreatedTS: true,
  // lastCompletedWorkoutCompletedTS: true,
  // lastCompletedWorkoutCompletionNumber: true,
  // lastCompletedWorkoutRating: true,

  // adds 10s to query time
  // numTeamJoins: true,

  // adds 8s to query time
  // healthStatus: true,

  // adds 2s to query time
  // recommendedTeamIDs: true,

  // adds 6s to query time
  // recentTeamHistory: {
  //   teamID: true,
  //   teamName: true,
  //   joinedTS: true,
  // },

  // adds 5s to query time
  // roomFlagsAreActive: true,

  // adds 1s to query time
  // latestTrialPeriodStartTS: true,
  // latestTrialPeriodEndTS: true,
  // latestTrialPeriodReason: true,

  teamID: true,

  utmSource: true,
  utmContent: true,
  utmCampaign: true,
  utmMedium: true,
  utmTerm: true,
  utmType: true,

  subscriptionStripeCustomerID: true,
  subscriptionStatus: true,
  subscriptionType: true,
  subscriptionIsCancelingAtPeriodEnd: true,
  subscriptionCurrentPeriodEndTS: true,
  subscriptionCurrentPeriodStartTS: true,

  introTrialPeriodStartTS: true,
  introTrialPeriodEndTS: true,

  // flagSettings: {
  //   flagType: true,
  //   // muteStartTS: true,
  //   // muteEndTS: true,
  // },
};

export { useDetailedUser } from "ui/hooks/useDetailedUsers";

/**
 * Runs `start(signal)`, aborting it and rejecting with a `TimeoutError` if it
 * hasn't settled within `ms`. The timer is cleared either way. `outer` (e.g.
 * react-query's signal for a superseded key) aborts it too.
 */
export const withTimeout = (start, ms, outer) => {
  const controller = new AbortController();
  if (outer?.aborted) controller.abort();
  outer?.addEventListener?.("abort", () => controller.abort(), { once: true });
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      const error = new Error("Member search timed out");
      error.name = "TimeoutError";
      reject(error);
    }, ms);
  });
  return Promise.race([start(controller.signal), timeout]).finally(() =>
    clearTimeout(timer)
  );
};

/**
 * `totalCount` costs a count over the whole detailed_user view, which with a
 * name filter can outlast Rocky's statement timeout; callers that don't show
 * a total pass `totalCount: false`. `timeoutMs` fails a page that takes
 * longer, so a caller can offer Retry instead of waiting.
 */
export function useDetailedUsers(
  {
    node,
    filter,
    sort = [],
    active,
    first = 25,
    totalCount = true,
    timeoutMs,
  } = {},
  props = []
) {
  const { session } = useSession();
  // A null key reads as an options object to react-query 3, which then has no
  // queryFn ("Missing queryFn"); an inactive query keeps its key and waits.
  return useInfiniteQuery(
    ["detailedUsers", ...props],
    async ({ pageParam: after = "", signal: querySignal }) => {
      try {
        // Check if endpoint is defined
        if (!endpoint) {
          const error = new Error("GraphQL endpoint not defined");
          // Add additional context to the error
          error.name = "EndpointMissingError";
          error.metadata = { props, filter };
          throw error;
        }

        // Check if session is available
        if (!session) {
          const error = new Error("Authentication session not available");
          error.name = "SessionMissingError";
          error.metadata = { props };
          throw error;
        }

        const query = jsonToGraphQLQuery(
          {
            query: {
              detailedUsers: {
                __args: {
                  first,
                  after,
                  filter,
                  sort,
                },
                ...(totalCount ? { totalCount: true } : {}),
                pageInfo: {
                  hasPreviousPage: true,
                  hasNextPage: true,
                  startCursor: true,
                  endCursor: true,
                },
                debug: {
                  sqlText: true,
                },
                edges: {
                  cursor: true,
                  node,
                },
              },
            },
          },
          { pretty: true }
        );
        
        // Execute GraphQL request with proper error handling
        const send = (signal) =>
          request({
            url: endpoint,
            document: query,
            requestHeaders: {
              Authorization: `Bearer ${session}`,
              "Content-Type": "application/json",
            },
            signal,
          });
        // react-query aborts `querySignal` when a newer key replaces this one.
        const response = timeoutMs
          ? await withTimeout(send, timeoutMs, querySignal)
          : await send(querySignal);

        // Validate response structure to catch unexpected response formats
        if (!response?.detailedUsers) {
          const error = new Error("Invalid response structure from detailedUsers query");
          error.name = "InvalidResponseError";
          error.metadata = { 
            responseKeys: Object.keys(response || {}),
            props,
            filter: JSON.stringify(filter).slice(0, 200) // Truncate for Sentry
          };
          throw error;
        }

        return response;
      } catch (error) {
        // Enhance error with additional context before propagating it
        if (!error.metadata) {
          error.metadata = {};
        }

        // Add context about the query
        error.metadata.queryName = "detailedUsers";
        error.metadata.filterCount = filter ? Object.keys(filter).length : 0;
        error.metadata.sortCount = sort ? sort.length : 0;
        error.metadata.requestedNodeKeys = node ? Object.keys(node).length : 0;

        // Skip Sentry capture for replica conflicts — React Query retries
        // up to 3x. onError captures if retries exhaust. See sibling
        // useDetailedUser above for the same pattern.
        // A timeout is the caller's to show (Retry), and an abort is a
        // superseded search; neither is a crash. A caller that sets
        // `timeoutMs` expects slow scans, so Rocky's own statement timeout
        // (57014) is the same case for it.
        const statementTimeout = (
          error?.response?.errors?.[0]?.message || error?.message || ""
        ).includes("57014");
        if (
          isReplicaConflict(error) ||
          error.name === "TimeoutError" ||
          error.name === "AbortError" ||
          (timeoutMs && statementTimeout)
        ) {
          error.tags = {
            ...error.tags,
            component: "useDetailedUsers",
            graphql: "query"
          };
          throw error;
        }

        // Capture the error to Sentry with detailed context
        Sentry.withScope((scope) => {
          // Add tags for better categorization
          scope.setTag("component", "useDetailedUsers");
          scope.setTag("graphql", "query");
          scope.setTag("operation", "detailedUsers");
          
          // Add context data
          scope.setContext("query", {
            filterSummary: filter ? Object.keys(filter) : [],
            sortSummary: sort || [],
            nodeKeysCount: node ? Object.keys(node).length : 0,
            first,
            after: after ? true : false,
            active
          });
          
          // Additional metadata that might help debug the issue
          if (error.response) {
            scope.setContext("graphqlResponse", {
              status: error.response.status,
              statusText: error.response.statusText,
              headers: Object.fromEntries(
                Object.entries(error.response.headers || {}).slice(0, 5)
              ),
              hasData: !!error.response.data,
              hasErrors: !!(error.response.errors?.length)
            });
          }
          
          Sentry.captureException(error);
        });
        
        // Let error information propagate to query error handlers
        error.tags = {
          ...error.tags,
          component: "useDetailedUsers",
          graphql: "query"
        };
        
        // Log error to console for local development
        console.error("[useDetailedUsers] GraphQL query error:", error);
        
        // Rethrow the enhanced error
        throw error;
      }
    },
    {
      enabled: !!active,
      retry: (failureCount, error) =>
        isReplicaConflict(error) && failureCount < 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      onError: (error) => {
        // Fires after retries exhaust. Captures replica conflicts that
        // got through all 3 attempts.
        if (isReplicaConflict(error)) {
          Sentry.withScope((scope) => {
            scope.setTag("component", "useDetailedUsers");
            scope.setTag("graphql", "query");
            scope.setTag("operation", "detailedUsers");
            scope.setTag("error_type", "replica_conflict_after_retries");
            Sentry.captureException(error);
          });
        }
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage?.detailedUsers?.edges?.length) return;
        if (!lastPage?.detailedUsers?.pageInfo?.hasNextPage) return;

        try {
          const { cursor } =
            lastPage.detailedUsers.edges[lastPage.detailedUsers.edges.length - 1];
          return cursor;
        } catch (error) {
          // Add context to pagination errors
          error.name = "PaginationError";
          error.metadata = {
            edgesLength: lastPage?.detailedUsers?.edges?.length,
            hasNextPage: lastPage?.detailedUsers?.pageInfo?.hasNextPage
          };
          
          // Capture pagination errors to Sentry
          Sentry.withScope((scope) => {
            scope.setTag("component", "useDetailedUsers");
            scope.setTag("error_type", "pagination");
            scope.setTag("operation", "getNextPageParam");
            
            // Add last page context to help debug pagination issues
            scope.setContext("paginationData", {
              hasEdges: !!lastPage?.detailedUsers?.edges,
              edgesLength: lastPage?.detailedUsers?.edges?.length || 0,
              hasPageInfo: !!lastPage?.detailedUsers?.pageInfo,
              hasNextPage: lastPage?.detailedUsers?.pageInfo?.hasNextPage,
              endCursor: lastPage?.detailedUsers?.pageInfo?.endCursor,
              totalCount: lastPage?.detailedUsers?.totalCount
            });
            
            Sentry.captureException(error);
          });
          
          // Log in development
          console.error("[useDetailedUsers] Pagination error:", error);
          
          throw error;
        }
      },
    }
  );
}
